import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { PaymentEncData } from '../models/payment-enc-data';
import { CustomerAction } from '../models/customer-action';
import { isMobile } from './constants';

/**
 * Returns true when the response is a Netopia v2 payment.
 */
export function isNetopiaV2(responseObject: PaymentEncData): boolean {
  return !!responseObject?.isV2;
}

/**
 * Handles the gateway redirect for both Netopia v1 (hidden form POST) and
 * Netopia v2 (hosted page redirect or 3DS form POST).
 *
 * V1: POSTs encrypted env_key + data to the MobilPay gateway URL.
 *     On mobile: opens MobileCardPaymentComponent in a new window instead.
 *
 * V2 has three sub-cases:
 *   - customerAction.type = "Authentication3D" → POSTs formData to the bank ACS URL;
 *     after 3DS the ACS POSTs to the backend payment-3ds-verify which calls verify-auth and
 *     redirects the browser to /{subdomain}/payment-return/{orderId}.
 *   - No customerAction + url present → frictionless hosted page: redirect to Netopia's page.
 *   - No customerAction + no url → direct-card frictionless (auto-approved, status 3):
 *     navigate directly to /{subdomain}/payment-return.
 */
export function redirectToPaymentGateway(
  responseObject: PaymentEncData,
  subdomain: string,
  router: Router
): void {
  if (isNetopiaV2(responseObject)) {
    redirectV2(responseObject, subdomain, router);
  } else {
    redirectV1(responseObject, subdomain, router);
  }
}

/**
 * Builds the mobile-card-payment URL used to pass payment credentials to the
 * MobileCardPaymentComponent running inside a native browser window.
 */
export function buildMobilePaymentUrl(
  responseObject: PaymentEncData,
  subdomain: string
): string {
  const baseUrl = environment.sso.mobileRedirectUrl;

  if (isNetopiaV2(responseObject)) {
    if (responseObject.customerAction?.type === 'Authentication3D') {
      return (
        `${baseUrl}/${subdomain}/mobile-card-payment` +
        `#v2AcsUrl=${encodeURIComponent(responseObject.customerAction.url)}` +
        `&v2FormData=${encodeURIComponent(JSON.stringify(responseObject.customerAction.formData ?? {}))}`
      );
    }
    return (
      `${baseUrl}/${subdomain}/mobile-card-payment` +
      `#v2PaymentUrl=${encodeURIComponent(responseObject.url)}`
    );
  }

  return (
    `${baseUrl}/${subdomain}/mobile-card-payment` +
    `#paymentEnvKey=${encodeURIComponent(responseObject.env_key)}` +
    `&paymentData=${encodeURIComponent(responseObject.data)}` +
    `&paymentUrl=${encodeURIComponent(responseObject.url)}`
  );
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function redirectV1(
  responseObject: PaymentEncData,
  subdomain: string,
  router: Router
): void {
  if (isMobile()) {
    const paymentPageUrl = buildMobilePaymentUrl(responseObject, subdomain);
    window.open(paymentPageUrl);
    router.navigate([`/${subdomain}/benefits-history/all`]);
    return;
  }

  const form = document.createElement('form');
  form.method = 'POST';
  form.action = responseObject.url;

  const envKeyInput = document.createElement('input');
  envKeyInput.type = 'hidden';
  envKeyInput.name = 'env_key';
  envKeyInput.value = responseObject.env_key;
  form.appendChild(envKeyInput);

  const dataInput = document.createElement('input');
  dataInput.type = 'hidden';
  dataInput.name = 'data';
  dataInput.value = responseObject.data;
  form.appendChild(dataInput);

  document.body.appendChild(form);
  form.submit();
}

function redirectV2(
  responseObject: PaymentEncData,
  subdomain: string,
  router: Router
): void {
  const needs3DS = responseObject.customerAction?.type === 'Authentication3D';

  if (needs3DS) {
    submit3DSForm(responseObject.customerAction);
  } else if (responseObject.url) {
    window.location.href = responseObject.url;
  } else {
    // Direct-card frictionless: payment already approved (status 3), no paymentURL
    router.navigate([`/${subdomain}/payment-return`], { queryParams: { orderId: responseObject.orderId } });
  }
}

/** Creates and submits a hidden form to the bank ACS URL for 3DS authentication.
 *  After the user authenticates, the ACS POSTs its response to the backend
 *  /api/payment/payment-3ds-verify, which calls verify-auth and redirects to payment-return.
 */
function submit3DSForm(customerAction: CustomerAction): void {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = customerAction.url;

  for (const [key, value] of Object.entries(customerAction.formData ?? {})) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = key;
    input.value = value;
    form.appendChild(input);
  }

  document.body.appendChild(form);
  form.submit();
}
